import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Search, Zap, Clock, Flame, MapPin, Users } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { GIG_TAGS, TAG_LABEL, type GigTag } from "@/lib/gigTags";
import { SKILL_LABEL, type WorkerSkill } from "@/lib/workerSkills";
import { NotificationsBell } from "@/components/NotificationsBell";

interface GigRow {
  id: string;
  title: string;
  description: string;
  tag: GigTag;
  budget_cents: number;
  type: "gig" | "drop";
  status: string;
  location: string | null;
  expires_at: string | null;
  created_at: string;
  poster_id: string;
  start_time: string | null;
  end_time: string | null;
  workers_needed: number;
  urgency: "normal" | "urgent" | "now";
  payout_type: "fixed" | "hourly";
  skill_tags: WorkerSkill[] | null;
  gps_lat: number | null;
  gps_lng: number | null;
  instant_accept: boolean;
}

type Tab = "browse" | "urgent" | "nearby" | "creator";

const formatExpiry = (iso: string | null) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / 3600000);
  if (h < 1) return `${Math.floor(ms / 60000)}m left`;
  if (h < 24) return `${h}h left`;
  return `${Math.floor(h / 24)}d left`;
};

const haversine = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const Gigs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [gigs, setGigs] = useState<GigRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState<GigTag | "all">("all");
  const [tab, setTab] = useState<Tab>("browse");
  const [mySkills, setMySkills] = useState<WorkerSkill[]>([]);
  const [creatorIds, setCreatorIds] = useState<Set<string>>(new Set());
  const [myLoc, setMyLoc] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("gigs")
        .select("*")
        .eq("status", "open")
        .order("created_at", { ascending: false })
        .limit(200);
      setGigs((data as unknown as GigRow[]) ?? []);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("worker_skills")
        .select("skill")
        .eq("user_id", user.id);
      setMySkills(((data ?? []) as { skill: WorkerSkill }[]).map((r) => r.skill));
    })();
  }, [user]);

  // Identify creator-posted gigs
  useEffect(() => {
    if (gigs.length === 0) return;
    const ids = Array.from(new Set(gigs.map((g) => g.poster_id)));
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("user_id, is_creator")
        .in("user_id", ids);
      setCreatorIds(
        new Set(((data ?? []) as { user_id: string; is_creator: boolean }[])
          .filter((p) => p.is_creator)
          .map((p) => p.user_id)),
      );
    })();
  }, [gigs]);

  // Geolocate for "nearby" tab
  useEffect(() => {
    if (tab !== "nearby" || myLoc || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setMyLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { timeout: 8000 },
    );
  }, [tab, myLoc]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let list = gigs.filter((g) => {
      if (tag !== "all" && g.tag !== tag) return false;
      if (g.expires_at && new Date(g.expires_at).getTime() < Date.now()) return false;
      if (
        ql &&
        !`${g.title} ${g.description} ${g.location ?? ""}`.toLowerCase().includes(ql)
      )
        return false;
      return true;
    });

    if (tab === "urgent") {
      list = list.filter((g) => g.urgency === "urgent" || g.urgency === "now" || g.type === "drop");
    } else if (tab === "creator") {
      list = list.filter((g) => creatorIds.has(g.poster_id));
    } else if (tab === "nearby" && myLoc) {
      list = list
        .filter((g) => g.gps_lat != null && g.gps_lng != null)
        .map((g) => ({
          g,
          d: haversine(myLoc, { lat: g.gps_lat as number, lng: g.gps_lng as number }),
        }))
        .sort((a, b) => a.d - b.d)
        .map((x) => x.g);
    }

    // Recommended boost: skill overlap surfaces first within Browse
    if (tab === "browse" && mySkills.length > 0) {
      list = [...list].sort((a, b) => {
        const aMatch = (a.skill_tags ?? []).filter((s) => mySkills.includes(s)).length;
        const bMatch = (b.skill_tags ?? []).filter((s) => mySkills.includes(s)).length;
        if (aMatch !== bMatch) return bMatch - aMatch;
        const ua = a.urgency === "now" ? 2 : a.urgency === "urgent" ? 1 : 0;
        const ub = b.urgency === "now" ? 2 : b.urgency === "urgent" ? 1 : 0;
        return ub - ua;
      });
    }

    return list;
  }, [gigs, q, tag, tab, mySkills, creatorIds, myLoc]);

  const nowGigs = useMemo(
    () => gigs.filter((g) => g.urgency === "now" && (!g.expires_at || new Date(g.expires_at).getTime() > Date.now())),
    [gigs],
  );

  const tabs: { id: Tab; label: string }[] = [
    { id: "browse", label: "Browse" },
    { id: "urgent", label: "Urgent" },
    { id: "nearby", label: "Nearby" },
    { id: "creator", label: "Creator" },
  ];

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)} right={<NotificationsBell />} />
      <div className="px-4 pt-2 pb-3 space-y-3">
        <div className="flex items-center justify-between">
          <h1 className="font-display font-extrabold text-2xl">Hustle Hub</h1>
          <Button size="sm" onClick={() => navigate("/gigs/new")} className="rounded-full">
            <Plus className="h-4 w-4 mr-1" /> Post
          </Button>
        </div>
        <div className="flex gap-2 text-xs">
          <Link to="/my-gigs" className="flex-1 text-center rounded-full bg-secondary px-3 py-2 font-semibold">
            My Gigs
          </Link>
          <Link to="/business" className="flex-1 text-center rounded-full bg-secondary px-3 py-2 font-semibold">
            My Jobs Posted
          </Link>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search gigs"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 rounded-full"
          />
        </div>

        <div className="flex gap-1 rounded-full bg-muted p-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                tab === t.id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {nowGigs.length > 0 && tab !== "urgent" && (
          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              setTab("urgent");
            }}
            className="flex items-center gap-2 rounded-2xl border border-destructive/40 bg-destructive/10 text-destructive px-3 py-2"
          >
            <Flame className="h-4 w-4" />
            <span className="text-xs font-bold uppercase">{nowGigs.length} need workers NOW</span>
            <span className="ml-auto text-xs font-semibold">View →</span>
          </Link>
        )}

        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 no-scrollbar">
          <button
            onClick={() => setTag("all")}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border ${
              tag === "all"
                ? "bg-foreground text-background border-foreground"
                : "bg-card border-border"
            }`}
          >
            All
          </button>
          {GIG_TAGS.map((t) => (
            <button
              key={t}
              onClick={() => setTag(t)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border ${
                tag === t
                  ? "bg-foreground text-background border-foreground"
                  : "bg-card border-border"
              }`}
            >
              {TAG_LABEL[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pb-24 space-y-3">
        {loading ? (
          <div className="grid place-items-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : tab === "nearby" && !myLoc ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Allow location to see jobs near you.
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center space-y-3">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-primary/10 grid place-items-center text-primary">
              <Plus className="h-6 w-6" />
            </div>
            <p className="text-sm text-muted-foreground">No gigs match your filters.</p>
            <button
              onClick={() => navigate("/gigs/new")}
              className="text-sm font-semibold text-primary hover:underline"
            >
              Post the first one →
            </button>
          </div>
        ) : (
          filtered.map((g) => {
            const expiry = formatExpiry(g.expires_at);
            const matched = (g.skill_tags ?? []).filter((s) => mySkills.includes(s));
            const dist =
              myLoc && g.gps_lat != null && g.gps_lng != null
                ? haversine(myLoc, { lat: g.gps_lat, lng: g.gps_lng })
                : null;
            return (
              <Link
                key={g.id}
                to={`/gigs/${g.id}`}
                className={`block rounded-2xl border p-4 hover:bg-muted/40 transition-colors ${
                  g.urgency === "now"
                    ? "bg-destructive/5 border-destructive/40"
                    : "bg-card border-border"
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h3 className="font-display font-bold text-base leading-tight">{g.title}</h3>
                  <span className="font-display font-extrabold text-primary shrink-0">
                    {formatMoney(g.budget_cents)}
                    {g.payout_type === "hourly" && (
                      <span className="text-[10px] font-semibold text-muted-foreground">/hr</span>
                    )}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">{g.description}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide bg-muted px-2 py-0.5 rounded-full">
                    {TAG_LABEL[g.tag]}
                  </span>
                  {g.urgency === "now" && (
                    <span className="text-[10px] font-bold uppercase bg-destructive text-destructive-foreground px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Flame className="h-3 w-3" /> Now
                    </span>
                  )}
                  {g.urgency === "urgent" && (
                    <span className="text-[10px] font-bold uppercase bg-primary/15 text-primary px-2 py-0.5 rounded-full">
                      Urgent
                    </span>
                  )}
                  {g.instant_accept && (
                    <span className="text-[10px] font-bold uppercase bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Zap className="h-3 w-3" /> Instant
                    </span>
                  )}
                  {g.workers_needed > 1 && (
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Users className="h-3 w-3" /> {g.workers_needed}
                    </span>
                  )}
                  {expiry && (
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {expiry}
                    </span>
                  )}
                  {dist != null && (
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {dist < 1 ? `${Math.round(dist * 1000)}m` : `${dist.toFixed(1)}km`}
                    </span>
                  )}
                  {g.location && dist == null && (
                    <span className="text-[10px] text-muted-foreground">📍 {g.location}</span>
                  )}
                  {matched.length > 0 && (
                    <span className="text-[10px] font-bold uppercase bg-foreground text-background px-2 py-0.5 rounded-full">
                      {matched.length} skill match
                    </span>
                  )}
                </div>
                {(g.skill_tags ?? []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(g.skill_tags ?? []).slice(0, 5).map((s) => (
                      <span
                        key={s}
                        className={`text-[10px] px-1.5 py-0.5 rounded-md border ${
                          mySkills.includes(s)
                            ? "border-primary text-primary"
                            : "border-border text-muted-foreground"
                        }`}
                      >
                        {SKILL_LABEL[s]}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            );
          })
        )}
      </div>
    </MobileShell>
  );
};

export default Gigs;
